import CoreUtils from '../common/utils/CoreUtils';
import NumberUtils from '../common/utils/NumberUtils';
import DateTimeUtils from '../common/utils/DateTimeUtils';

import QuickEntryConfigurationUtils from '../common/value-types/QuickEntryConfigurationUtils';

import EnergyCellViewTemplate from '../editors/energy-cell-inplace/EnergyCellViewTemplate';
import EnergyCellEditTemplate from '../editors/energy-cell-inplace/EnergyCellEditTemplate';

import ScheduleColumnSettings from './ScheduleColumnSettings';

const hlhLabel = 'HLH';
const llhLabel = 'LLH';
const hldLabel = 'HLD';
const lldLabel = 'LLD';
const atcLabel = 'ATC';
const quantityLabel = 'QTY';

const hlh_llh_scheme = 'HLH_LLH';
const defaultLabels = 'Default';
const loadHourlyLabels = 'HLH_LLH_ATC';
const quantityLabels = 'QTY_QTY_QTY';

export default class ScheduleColumnManager {
  constructor(session, counterparty) {
    this.locale = session.locale;
    this.timeZoneShortName = session.timeZoneShortName;
    this.quickEntryConfiguration = QuickEntryConfigurationUtils.deserializeValues(counterparty.quickEntryConfiguration);
    this.heavyLightHourScheme = counterparty.heavyLightHourScheme;
    this.quickEntryLabels = counterparty.quickEntryLabels;

    this.columns = [];
    this.energyFields = this.resolveEnergyFields();

    this.settings = new ScheduleColumnSettings(counterparty.preferenceGroupName);
  }

  addLeadingColumns(columns) { }
  addIntermediateColumns(columns) { }
  addTrailingColumns(columns) { }
  getColumnCssClass() { }

  shouldUseHlhLabels() {
    return this.quickEntryLabels === loadHourlyLabels
      || (this.quickEntryLabels === defaultLabels && this.heavyLightHourScheme === hlh_llh_scheme);
  }

  shouldUseQuantityLabels() {
    return this.quickEntryLabels === quantityLabels;
  }

  getColumns() {
    var columns = [];

    this.addLeadingColumns(columns);

    this.addColumn(columns, {
      alignment: 'left',
      allowEditing: false,
      allowFiltering: false,
      allowHiding: false,
      allowResizing: true,
      dataType: 'date',
      caption: 'Date',
      dataField: 'scheduleDate',
      format: this.formatShortDate.bind(this),
      visible: true,
      width: 90
    });

    this.addColumn(columns, {
      alignment: 'left',
      allowEditing: false,
      allowFiltering: false,
      allowHiding: true,
      allowResizing: true,
      dataType: 'date',
      caption: 'Week Of',
      dataField: 'weekOf',
      format: this.formatShortDate.bind(this),
      visible: false,
      width: 90
    });

    this.addWeekDayColumns(columns);

    this.addIntermediateColumns(columns);

    this.addColumn(columns, {
      alignment: 'left',
      allowEditing: false,
      allowFiltering: false,
      allowHiding: true,
      allowResizing: true,
      dataType: 'date',
      caption: 'Trading Date',
      dataField: 'tradingDate',
      format: this.formatShortDate.bind(this),
      visible: false,
      width: 90
    });

    this.addQuickEntryColumns(columns);
   
    CoreUtils.iterateProperties(this.energyFields, (_, energyField) =>
      this.addColumn(columns, {
        alignment: 'center',
        allowEditing: true,
        allowFiltering: false,
        allowGrouping: false,
        allowHiding: false,
        allowResizing: false,
        allowSorting: false,
        caption: energyField.caption,
        cellTemplate: EnergyCellViewTemplate,
        dataField: energyField.dataField,
        dataType: 'object',
        editCellTemplate: EnergyCellEditTemplate,
        visible: energyField.visible
      }));

    this.addTrailingColumns(columns);

    this.addColumn(columns, {
      alignment: 'left',
      allowEditing: false,
      allowFiltering: false,
      allowHiding: true,
      allowResizing: true,
      dataType: 'datetime',
      caption: 'Last Updated (' + this.timeZoneShortName + ')',
      dataField: 'lastUpdatedTime',
      format: this.formatShortDateTime.bind(this),
      visible: false,
      width: 90
    });

    this.addColumn(columns, {
      alignment: 'left',
      allowEditing: false,
      allowFiltering: false,
      allowHiding: true,
      allowResizing: true,
      dataType: 'string',
      caption: 'Last Updated By',
      dataField: 'lastUpdatedUserShort',
      visible: false,
      width: 90
    });

    this.columns = columns;
    return columns;
  }

  addColumn(columns, column) {
    column.cssClass = this.getColumnCssClass();
    this.settings.enactColumnSettings(column);
    columns.push(column);
  }

  resolveEnergyFields() {
    var energyFields = {};

    for (var heIndex = 0; heIndex < 24; heIndex++) {
      var dataField = 'he' + NumberUtils.padZerosIfNeeded(heIndex + 1, 2);

      energyFields[dataField] = {
        caption: 'HE' + NumberUtils.padZerosIfNeeded(heIndex + 1, 2),
        dataField: dataField,
        heIndex: heIndex,
        visible: heIndex !== 24
      };

      if (heIndex === 1) {
        energyFields.he25 = {
          caption: 'HE02*',
          dataField: 'he25',
          heIndex: 24,
          visible: false
        };
      }
    }

    return energyFields;
  }

  updateColumnVisibility(dataGrid, activeDataFields) {
    CoreUtils.iterateProperties(this.energyFields, (_, energyField) => {
      energyField.visible = activeDataFields.includes(energyField.dataField) || energyField.heIndex !== 24;

      if (CoreUtils.isDefinedAndNotNull(dataGrid))
        dataGrid.columnOption(energyField.dataField, 'visible', energyField.visible);
    });
  }

  formatShortDate(value) {
    return DateTimeUtils.formatShortDate(value, this.locale);
  }

  formatShortDateTime(value) {
    return DateTimeUtils.formatShortDateTime(value, this.locale);
  }

  formatNumber(value) {
    return NumberUtils.formatNumber(value, this.locale);
  }

  addHlhQuickEntryColumn(columns) {
    if (CoreUtils.isDefinedAndNotNull(this.quickEntryConfiguration)) {
      if (this.quickEntryConfiguration.hlh)
       this.addColumn(columns, {
          alignment: 'center',
          allowEditing: true,
          allowFiltering: false,
          allowGrouping: false,
          allowHiding: false,
          allowResizing: false,
          allowSorting: false,
          caption: this.shouldUseQuantityLabels()
              ? quantityLabel
              : this.shouldUseHlhLabels()
                  ? hlhLabel
                  : hldLabel,
          cellTemplate: EnergyCellViewTemplate,
          dataField: 'hlhEntryField',
          dataType: 'object',
          editCellTemplate: EnergyCellEditTemplate,
          visible: true
       });
    }
  }

  addLlhQuickEntryColumn(columns) {
    if (CoreUtils.isDefinedAndNotNull(this.quickEntryConfiguration)) {
      if (this.quickEntryConfiguration.llh)
       this.addColumn(columns, {
          alignment: 'center',
          allowEditing: true,
          allowFiltering: false,
          allowGrouping: false,
          allowHiding: false,
          allowResizing: false,
          allowSorting: false,
          caption: this.shouldUseQuantityLabels()
              ? quantityLabel
              : this.shouldUseHlhLabels()
                  ? llhLabel
                  : lldLabel,
          cellTemplate: EnergyCellViewTemplate,
          dataField: 'llhEntryField',
          dataType: 'object',
          editCellTemplate: EnergyCellEditTemplate,
          visible: true
       });
    }
  }

  addAtcQuickEntryColumn(columns) {
    if (CoreUtils.isDefinedAndNotNull(this.quickEntryConfiguration)) {
      if (this.quickEntryConfiguration.atc)
       this.addColumn(columns, {
          alignment: 'center',
          allowEditing: true,
          allowFiltering: false,
          allowGrouping: false,
          allowHiding: false,
          allowResizing: false,
          allowSorting: false,
          caption: this.shouldUseQuantityLabels()
              ? quantityLabel
              : atcLabel,
          cellTemplate: EnergyCellViewTemplate,
          dataField: 'atcEntryField',
          dataType: 'object',
          editCellTemplate: EnergyCellEditTemplate,
          visible: true
       });
    }
  }

  addQuickEntryColumns(columns) {
    this.addLlhQuickEntryColumn(columns);
    this.addHlhQuickEntryColumn(columns);
    this.addAtcQuickEntryColumn(columns);
  }

  addWeekDayColumn(columns) {
    this.addColumn(columns, {
      alignment: 'center',
      allowEditing: false,
      allowFiltering: false,
      allowHiding: true,
      allowResizing: true,
      dataType: 'string',
      caption: 'Weekday',
      dataField: 'weekday',
      visible: false,
      width: 70 
    });
  }

  addWeekDayColumns(columns) {
    this.addWeekDayColumn(columns);
  }
}